import type { DeskDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Timer } from 'lucide-react';
import { Section } from '@/components/section';
import { api } from '@/lib/api';
import { intlLocale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/** A duration in hours, the way people say it: "under 1 h", "7 h", "2.5 days". */
export function formatHours(hours: number | null): string {
  if (hours === null) return '–';
  if (hours < 1) return t('under 1 h');
  if (hours < 48) return t('{n} h', { n: Math.round(hours) });
  return t('{n} days', { n: new Intl.NumberFormat(intlLocale, { maximumFractionDigits: 1 }).format(hours / 24) });
}

/**
 * How long the desk's work takes, per Work Item type (docs/features/desk-pulse.md#cycle-times): how many were done in
 * the last 90 days, how many are open, and the time from created to complete — the median (what is usual) and the
 * average (pulled up by the slow ones) — plus the oldest open one.
 */
export function DeskCycleTimes({ desk }: { desk: DeskDetail }) {
  const stats = useQuery({ queryKey: qk.deskWorkItemStats(desk.id), queryFn: () => api.desks.workItemStats(desk.id) });
  if (!stats.data?.types.length) return null;
  const plural = desk.workItemLabel.plural;
  return (
    <Section
      icon={<Timer />}
      title={t('Cycle time')}
      description={t('{items} per type: done in the last {days} days, open, and how long one takes from created to complete.', { items: plural, days: stats.data.days })}
      contentClassName="px-0 pb-0"
    >
      <div className="overflow-x-auto border-t">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr className="border-b">
              <th className="px-5 py-2 font-medium">{t('Type')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('Done')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('Open')}</th>
              <th className="px-3 py-2 text-right font-medium" title={t('What is usual: half take less, half take longer.')}>
                {t('Median')}
              </th>
              <th className="px-3 py-2 text-right font-medium" title={t('The average, pulled up by the slow ones.')}>
                {t('Average')}
              </th>
              <th className="px-5 py-2 text-right font-medium">{t('Oldest open')}</th>
            </tr>
          </thead>
          <tbody>
            {stats.data.types.map((row) => (
              <tr key={row.typeId ?? 'none'} className="border-b last:border-0">
                <td className="px-5 py-2.5 font-medium">{row.name ?? t('Without a type')}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{row.done}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{row.open}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatHours(row.medianHours)}</td>
                <td className="px-3 py-2.5 text-right text-muted-foreground tabular-nums">{formatHours(row.avgHours)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums">{row.open ? formatHours(row.oldestOpenHours) : '–'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
